import { useQuery } from "@tanstack/vue-query"
import { PatientConversationRoleEnum, PatientConversationUserRefDTO } from "@vitable/asclepius-api-client-ts"
import { useAsclepius } from "@/composables/api/asclepius"
import {
  patientConversationKeys,
  retryTransportFailuresOnly
} from "@/modules/patient-conversations/composables/patient-conversations"
import { roleLabel } from "@/modules/patient-conversations/constants/patient-conversation-labels"

const DIRECTORY_STALE_TIME_MS = 5 * 60_000

/** A staff member as the pickers list them: a `usr_…` id, a name and a display role. */
export interface PatientConversationStaffOption {
  id: string
  fullName: string
  role: PatientConversationRoleEnum
  /** Unique within the directory: the name, or "Name (Role)" / "Name (Role · 1a2b)" for namesakes. */
  displayName: string
}

/** Name search shared by the assignee pickers and the composer's "@" picker: case-insensitive substring. */
export function filterStaffByName(
  staff: ReadonlyArray<PatientConversationStaffOption>,
  query: string
): ReadonlyArray<PatientConversationStaffOption> {
  const needle = query.trim().toLowerCase()
  return needle ? staff.filter((person) => person.displayName.toLowerCase().includes(needle)) : staff
}

type StaffRef = Omit<PatientConversationStaffOption, "displayName">

function toStaffRef(person: PatientConversationUserRefDTO): StaffRef {
  return { id: person.id, fullName: person.full_name, role: person.role }
}

/** A note pages people by name, so namesakes get their role (and, failing that, an id tail) to tell them apart. */
function withUniqueDisplayNames(staff: ReadonlyArray<StaffRef>): Array<PatientConversationStaffOption> {
  const tally = (key: (person: StaffRef) => string): Map<string, number> =>
    staff.reduce((counts, person) => counts.set(key(person), (counts.get(key(person)) ?? 0) + 1), new Map())
  const byName = tally((person) => person.fullName)
  const byNameAndRole = tally((person) => `${person.fullName}|${person.role}`)

  return staff.map((person) => {
    if ((byName.get(person.fullName) ?? 0) < 2) return { ...person, displayName: person.fullName }
    const role = roleLabel(person.role)
    const roleIsEnough = (byNameAndRole.get(`${person.fullName}|${person.role}`) ?? 0) < 2
    const tag = roleIsEnough ? role : `${role} · ${person.id.slice(-4)}`
    return { ...person, displayName: `${person.fullName} (${tag})` }
  })
}

/**
 * The pilot staff directory behind every staff picker (the composer's "@", Reassign, the bulk "Assign to" bar and
 * the New message assignee): active Providers and Clinical Admins ("Support Team") enabled for patient
 * conversations, the viewer included, sorted by name by the server. The same list for every viewer, so a Provider
 * can page or assign Support Team, and nobody is offered staff the server would refuse.
 */
export function usePatientConversationStaffDirectory() {
  const { getPatientConversationApi, callWithResponse } = useAsclepius()

  async function listStaff(): Promise<Array<PatientConversationStaffOption>> {
    const response = await callWithResponse(() => getPatientConversationApi().listPatientConversationStaff())
    return withUniqueDisplayNames(response.data.map(toStaffRef))
  }

  return useQuery({
    queryKey: patientConversationKeys.staff(),
    queryFn: listStaff,
    staleTime: DIRECTORY_STALE_TIME_MS,
    retry: retryTransportFailuresOnly
  })
}
